// XLBreadcrumbs - Sprint 44 XL.
// Trail de navegacion con separador y ultimo item sin link.
// Usa react-router via react-router-dom Link para items clickeables.
// Compatible con dark mode. Items son array [{label, to?}].
//
// Uso:
//   <XLBreadcrumbs items={[
//     { label: 'Dashboard', to: '/' },
//     { label: 'Ventas',    to: '/ventas' },
//     { label: 'VTA-0001' },  // sin `to` -> pagina actual
//   ]} />
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-breadcrumbs-css';
const CSS = `
.xl-crumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-family: var(--xl-font-sans);
  font-size: 13px;
  color: var(--xl-color-text-secondary);
}
.xl-crumbs__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.xl-crumbs__link {
  color: var(--xl-color-text-secondary);
  text-decoration: none;
  padding: 2px 4px;
  border-radius: var(--xl-radius-sm);
  transition: color 120ms cubic-bezier(0,0,0.2,1),
              background 120ms cubic-bezier(0,0,0.2,1);
}
.xl-crumbs__link:hover {
  color: var(--xl-color-text);
  background: var(--xl-color-surface-alt);
}
.xl-crumbs__link:focus-visible {
  outline: 2px solid var(--xl-color-focus);
  outline-offset: 1px;
}
.xl-crumbs__current {
  color: var(--xl-color-text);
  font-weight: 500;
}
.xl-crumbs__sep {
  color: var(--xl-color-text-muted);
  font-size: 12px;
  line-height: 1;
  user-select: none;
}
`;

function _sepOf(sep) {
  if (sep === 'slash') return '/';
  if (sep === 'dot')   return '\u00B7';
  return '\u203A'; // single right angle quote '>'
}

export default function XLBreadcrumbs({
  items = [],
  separator = 'chevron',
  className = '',
  ariaLabel = 'Breadcrumbs',
}) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  if (!items || items.length === 0) return null;
  const sep = _sepOf(separator);

  return (
    <nav className={'xl-crumbs ' + className} aria-label={ariaLabel}>
      <ol
        className="xl-crumbs"
        style={{ listStyle: 'none', margin: 0, padding: 0 }}
      >
        {items.map((it, idx) => {
          const isLast = idx === items.length - 1;
          const hasLink = !isLast && !!it.to;
          const key = (it.to || '') + '|' + it.label + '|' + idx;
          return (
            <li key={key} className="xl-crumbs__item">
              {hasLink && (
                <Link to={it.to} className="xl-crumbs__link">
                  {it.label}
                </Link>
              )}
              {!hasLink && (
                <span
                  className={isLast ? 'xl-crumbs__current' : 'xl-crumbs__link'}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {it.label}
                </span>
              )}
              {!isLast && (
                <span className="xl-crumbs__sep" aria-hidden="true">{sep}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
